import ClipboardImage from '../../images/clipboardv2.png';
import { Alert, Box, Button, Heading, Image, Page, Paragraph, Text } from '@zillow/constellation-10';
import { Link, Redirect } from 'react-router-dom';
import { manifest } from '../../app/routes';
import { renderPdf } from './pdfReceiptRenderer/pdfReceiptRenderer';
import { useAppGuestPay } from '../../hooks/useGuestPay';
import { logError, numberUtils, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import React from 'react';
import { PaymentType } from '../../model/PaymentType';
import {
  emitGuestPayConfirmationDownloadPDFEvent,
  emitGuestPayConfirmationPayAnotherEvent,
} from '../../model/GuestPayClickstreamEvents';
import type { AdproductsGuestPayZuoraDto } from '@zg-rentals/ts-adproducts-client';

interface SummaryRowProps {
  borderTop?: boolean;
  borderBottom?: boolean;
  children: React.ReactNode;
}

const SummaryRow = ({ borderTop, borderBottom, children }: SummaryRowProps) => (
  <Box
    css={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: 'md 0',
      borderTop: borderTop ? '1px solid' : undefined,
      borderBottom: borderBottom ? '1px solid' : undefined,
      borderColor: 'gray.200',
    }}
  >
    {children}
  </Box>
);

type PaymentSectionProps = {
  heading: string;
  invoices: Array<AdproductsGuestPayZuoraDto>;
  statusLabel?: string;
  statusColor?: string;
  totalLabel: string;
};

const PaymentSection = ({ heading, invoices, statusLabel, statusColor, totalLabel }: PaymentSectionProps) => {
  const balanceTotal = invoices.map((invoice) => invoice.balance).reduce((a, b) => a + b, 0);
  const totalTaxAmount = invoices.map((invoice) => invoice.invoiceTaxAmount ?? 0).reduce((a, b) => a + b, 0);
  const totalAmountDue = invoices.map((invoice) => invoice.amountDue).reduce((a, b) => a + b, 0);
  const taxAmount = (totalTaxAmount * balanceTotal) / totalAmountDue;

  return (
    <>
      <Heading level={4} css={{ marginTop: 'md' }}>
        {heading}
      </Heading>
      <Box css={{ marginBottom: 'lg', marginTop: 'md' }}>
        <Box>
          {invoices.map((invoice, i) => (
            <SummaryRow key={invoice.invoiceNumber} borderTop={i === 0}>
              <Box css={{ display: 'flex', gap: 'xs', alignItems: 'center', flex: 1 }}>
                <Text>{invoice.invoiceNumber}</Text>
                {statusLabel && (
                  <Text
                    css={{
                      fontSize: 'xs',
                      color: statusColor,
                      fontWeight: 'medium',
                    }}
                  >
                    ({statusLabel})
                  </Text>
                )}
              </Box>
              <Box css={{ textAlign: 'right' }}>
                <Text>{numberUtils.asCurrency(invoice.balance)}</Text>
              </Box>
            </SummaryRow>
          ))}
          <SummaryRow borderTop={true}>
            <Text>Balance</Text>
            <Text>{numberUtils.asCurrency(balanceTotal - taxAmount)}</Text>
          </SummaryRow>
          <SummaryRow>
            <Text>Taxes</Text>
            <Text>{numberUtils.asCurrency(taxAmount)}</Text>
          </SummaryRow>
          <SummaryRow borderBottom={true} borderTop={true}>
            <Box>
              <Text textStyle="heading-sm">{totalLabel}</Text>
            </Box>
            <Box css={{ textAlign: 'right' }}>
              <Text textStyle="heading-sm">{numberUtils.asCurrency(balanceTotal)}</Text>
            </Box>
          </SummaryRow>
        </Box>
      </Box>
    </>
  );
};

export const GuestPayConfirmationPage = () => {
  const { guestPayState } = useAppGuestPay();
  const { user } = useRentalManagerUser();

  const mainAccountName = React.useMemo(() => {
    return guestPayState?.invoices?.[0]?.accountName;
  }, [guestPayState?.invoices]);

  if (!guestPayState?.accountNumber) {
    return <Redirect to={manifest.billingDue} />;
  }
  const filteredInvoices = guestPayState?.invoices?.filter((invoice) =>
    guestPayState?.selectedInvoices?.has(invoice.invoiceNumber),
  );

  const paymentResults = guestPayState?.paymentResults || {};

  const successfulInvoices = filteredInvoices.filter(
    (invoice) => paymentResults[invoice.invoiceNumber]?.paymentSuccess,
  );
  const failedInvoices = filteredInvoices.filter(
    (invoice) => paymentResults[invoice.invoiceNumber] && !paymentResults[invoice.invoiceNumber].paymentSuccess,
  );
  const processingInvoices = filteredInvoices.filter((invoice) => !paymentResults[invoice.invoiceNumber]);

  const hasFailedPayments = failedInvoices.length > 0;
  const hasSuccessfulPayments = successfulInvoices.length > 0;

  const handleDownloadPdf = () => {
    if (!guestPayState?.accountNumber || !guestPayState?.paymentType) {
      return;
    }

    emitGuestPayConfirmationDownloadPDFEvent({ user });

    try {
      renderPdf({
        paymentType: guestPayState?.paymentType,
        invoices: filteredInvoices,
        accountNumber: guestPayState?.accountNumber,
        paymentResults: guestPayState?.paymentResults,
      });
    } catch (e) {
      logError({
        error: e,
        errorType: 'RenderPdfError',
        context: {
          details: 'GuestPayConfirmationPdf renderPdf error',
          paymentType: guestPayState?.paymentType,
          invoices: filteredInvoices,
          accountNumber: guestPayState?.accountNumber,
        },
      });
    }
  };

  const messageSuffix = guestPayState?.paymentType === PaymentType.CREDIT_CARD ? 'was received' : 'is processing';
  let message = `Thanks, your payment for ${mainAccountName} ${messageSuffix}`;

  if (hasFailedPayments && hasSuccessfulPayments) {
    message = `Some payments for ${mainAccountName} ${messageSuffix}`;
  } else if (hasFailedPayments && !hasSuccessfulPayments) {
    message = `Payment for ${mainAccountName} could not be processed`;
  }

  return (
    <Page.Root>
      <Page.Content>
        <Box css={{ background: 'white' }}>
          <Box
            css={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '3xl',
              marginBottom: 'xl',
            }}
          >
            <Image src={ClipboardImage} alt="Payment confirmation" css={{ height: '200px', paddingTop: 'lg' }} />
          </Box>
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <Box css={{ maxWidth: '500px', textAlign: 'center' }}>
              <Heading css={{ marginBottom: 'lg', fontFamily: 'serif' }} level={3}>
                {message}
              </Heading>
              {hasFailedPayments && (
                <Box css={{ marginBottom: 'md' }}>
                  <Alert tone="warning">
                    One or more invoice payments failed to process. Review your payment details for accuracy before
                    trying again, or try a different payment method.
                  </Alert>
                </Box>
              )}
              {successfulInvoices.length > 0 && (
                <PaymentSection
                  heading={
                    guestPayState?.paymentType === PaymentType.ACH ? 'Summary of charges' : 'Successful payments'
                  }
                  invoices={successfulInvoices}
                  totalLabel="Total paid"
                />
              )}
              {failedInvoices.length > 0 && (
                <PaymentSection
                  heading="Remaining payments"
                  invoices={failedInvoices}
                  statusLabel="Failed"
                  statusColor="red.600"
                  totalLabel="Total due"
                />
              )}
              {processingInvoices.length > 0 && (
                <PaymentSection heading="Processing" invoices={processingInvoices} totalLabel="Total processing" />
              )}
            </Box>
          </Box>
        </Box>
        <Box
          css={{
            background: 'gray.100',
            minHeight: '200px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Box css={{ maxWidth: '500px', textAlign: 'center' }}>
            <Paragraph textStyle="body" css={{ marginTop: 'xl', marginBottom: 'md' }}>
              Download a copy of this payment summary for your records.
            </Paragraph>
            <Button tone="brand" emphasis="filled" css={{ marginBottom: 'lg' }} onClick={handleDownloadPdf}>
              Download summary
            </Button>
            <Paragraph textStyle="body" css={{ marginBottom: 'md' }}>
              Return to the guest payment page to pay another invoice.
            </Paragraph>
            <Button tone="neutral" emphasis="outlined" css={{ marginBottom: 'xl' }} asChild>
              <Link to={manifest.guestPayPage} onClick={() => emitGuestPayConfirmationPayAnotherEvent({ user })}>
                Pay another invoice
              </Link>
            </Button>
          </Box>
        </Box>
      </Page.Content>
    </Page.Root>
  );
};
